import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { KeyRound, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { web, errMsg } from "../lib/api";
import { Loader, Empty, Btn } from "../components/app/kit";
import { Field, TextInput } from "../components/app/Form";
import { useInviteWebsite, siteLabel } from "../hooks/useInviteWebsite";

const Shell = ({ children }) => {
  const site = useInviteWebsite();
  return (
  <div className="flex min-h-screen items-center justify-center bg-[#0A0E1A] px-5 py-12">
    <div className="w-full max-w-md">
      <Link to="/" className="mb-8 block font-display text-2xl font-black uppercase tracking-tight text-white">GAME<span className="text-[#39FF14]">DAY</span></Link>
      {children}
      <p className="mt-6 text-center text-sm text-slate-400">New to GAMEDAY? <a href={site} target="_blank" rel="noreferrer" data-testid="invite-website-link" className="font-semibold text-white underline decoration-[#39FF14]/60 hover:text-[#39FF14]">Visit {siteLabel(site)}</a></p>
    </div>
  </div>
  );
};

const CodeStep = ({ id, onOk }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Enter the access code from your coach");
    setBusy(true);
    try { await web.post(`/coach-invites/${id}/verify`, { access_code: code.trim() }); onOk(code.trim()); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="Access code"><TextInput data-testid="accept-code-input" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" placeholder="HOOPS-1234" className="font-mono-gd" autoFocus /></Field>
      <Btn type="submit" disabled={busy} data-testid="accept-code-submit" className="w-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue<ArrowRight className="h-4 w-4" /></>}</Btn>
    </form>
  );
};

const PasswordStep = ({ id, code, invite }) => {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: invite.name ?? "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) return toast.error("Password needs at least 8 characters");
    if (f.password !== f.confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const { data } = await web.post(`/coach-invites/${id}/accept`, { access_code: code, password: f.password, name: f.name.trim() || null });
      toast.success(`Welcome to ${invite.team_name ?? "the team"}!`);
      if (data?.access_token) { await loginWithToken(data.access_token); navigate("/coach"); }
      else navigate("/login");
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="Your name"><TextInput data-testid="accept-name-input" value={f.name} onChange={set("name")} placeholder="Coach Rivera" /></Field>
      <Field label="Choose a password"><TextInput data-testid="accept-password-input" type="password" autoComplete="new-password" value={f.password} onChange={set("password")} /></Field>
      <Field label="Confirm password"><TextInput data-testid="accept-confirm-input" type="password" autoComplete="new-password" value={f.confirm} onChange={set("confirm")} /></Field>
      <p className="text-xs text-slate-400">You'll sign in with {invite.email} and this password on the website and in the GAMEDAY app.</p>
      <Btn type="submit" disabled={busy} data-testid="accept-submit" className="w-full">{busy ? "Joining…" : "Join the team"}</Btn>
    </form>
  );
};

export default function AcceptInvite() {
  const { inviteId } = useParams();
  const { data, loading, error } = useApi(`/coach-invites/${inviteId}`, web);
  const [code, setCode] = useState(null);
  if (loading) return <Shell><Loader /></Shell>;
  if (error || !data) return <Shell><div data-testid="invite-invalid"><Empty title="Invite not available" text="This invite expired, was cancelled or was already used. Ask the coach for a new one." /></div></Shell>;
  return (
    <Shell>
      <div data-testid="accept-invite-page" className="rounded-3xl border border-[#1E293B] bg-[#0F172A] p-6 sm:p-8">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#39FF14]/15"><KeyRound className="h-5 w-5 text-[#39FF14]" /></span>
        <p className="mt-5 font-mono-gd text-xs uppercase tracking-[0.25em] text-[#39FF14]">{({ coach: "Coach", manager: "Team manager", player: "Player", parent: "Parent" })[data.role] ?? "Coach"} invite</p>
        <h1 data-testid="invite-team-name" className="mt-2 font-display text-4xl font-black uppercase leading-none text-white">{data.team_name ?? "Your team"}</h1>
        <p className="mb-6 mt-3 text-sm text-slate-300">{data.invited_by ? `${data.invited_by} invited ` : "You're invited: "}<span className="text-white">{data.email}</span>{` to join as a ${data.role ?? "coach"}`}{data.player_name ? <> for <span className="text-white">{data.player_name}</span></> : null}. {code ? "Now set up your login." : "Enter the access code your coach sent with this link."}</p>
        {code ? <PasswordStep id={inviteId} code={code} invite={data} /> : <CodeStep id={inviteId} onOk={setCode} />}
      </div>
    </Shell>
  );
}
